import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";

// Fix default-marker icon URLs (Webpack/CRA strips them)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      if (onPick) onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);
  return null;
}

/**
 * GeofenceMap — interactive Leaflet map showing a site pin + radius circle.
 * Props:
 *  - lat, lng (number) — center (defaults to a neutral NYC view if null)
 *  - radius_m (number) — circle radius in metres
 *  - editable (bool) — click on map sets lat/lng via onPick
 *  - onPick (fn) — receives (lat, lng) when editable && user clicks
 *  - height (string) — CSS height, default "320px"
 */
// Recharts / react-leaflet props that never change per instance — extracted so
// they don't create new object identities on every render.
const GEOFENCE_PATH_OPTIONS = { color: "#FF3B30", fillColor: "#FF3B30", fillOpacity: 0.15 };

export function GeofenceMap({ lat, lng, radius_m = 200, editable = false, onPick, height = "320px" }) {
  const hasCoords = lat != null && lng != null && !Number.isNaN(Number(lat)) && !Number.isNaN(Number(lng));
  const center = hasCoords ? [Number(lat), Number(lng)] : [40.7128, -74.006];
  const zoom = hasCoords ? 16 : 11;
  return (
    <div style={{ height }} className="border border-[#27272A] overflow-hidden" data-testid="geofence-map">
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom={editable}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {hasCoords && (
          <>
            <Marker position={center} />
            <Circle center={center} radius={Number(radius_m) || 200} pathOptions={GEOFENCE_PATH_OPTIONS} />
            <Recenter lat={Number(lat)} lng={Number(lng)} />
          </>
        )}
        {editable && <ClickHandler onPick={onPick} />}
      </MapContainer>
      {editable && (
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 px-3 py-1 bg-[#0A0A0A] border-t border-[#27272A]">
          {hasCoords ? "Click the map to move the pin" : "Click the map to set this site's location"}
        </div>
      )}
    </div>
  );
}

export default GeofenceMap;
